import React from 'react';
import { User, ShieldCheck, LayoutDashboard, ArrowRight, X } from 'lucide-react';
import TetfundLogo from '../../assets/images/TetfundLogo.svg';

interface RoleOption {
  name: string;
  roleId: number;
}

interface AccountSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  roles: RoleOption[];
  onSelect: (roleName: string, roleId: number) => void;
  allowClose?: boolean;
}

interface RoleUIConfig {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  badge: string;
}

const AccountSelectionModal: React.FC<AccountSelectionModalProps> = ({
  isOpen,
  onClose,
  roles,
  onSelect,
  allowClose = true
}) => {
  if (!isOpen) return null;

  const getRoleConfig = (roleName: string): RoleUIConfig => {
    const name = roleName.toLowerCase();
    if (name.includes('author')) {
      return {
        id: 'author',
        title: 'Author',
        description: 'Submit and track your academic manuscripts and research papers.',
        icon: <User className="w-8 h-8" />,
        color: 'bg-emerald-500',
        badge: 'Submissions'
      };
    } else if (name.includes('reviewer') || name.includes('accessor')) {
      return {
        id: 'reviewer',
        title: 'Reviewer',
        description: 'Assess and provide expert feedback on submitted manuscripts.',
        icon: <ShieldCheck className="w-8 h-8" />,
        color: 'bg-amber-500',
        badge: 'Assessments'
      };
    } else {
      return {
        id: 'thematic_area',
        title: roleName,
        description: 'Manage and oversee thematic categories and lead reviews.',
        icon: <LayoutDashboard className="w-8 h-8" />,
        color: 'bg-indigo-500',
        badge: 'Management'
      };
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-[#F8FAFC] transition-opacity" 
        onClick={() => allowClose && onClose()}
      />

      {/* Modal Container */}
      <div className="relative w-full max-w-5xl bg-[#F8FAFC] rounded-[2.5rem] shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
        {/* Dynamic Background Elements */}
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-emerald-100 rounded-full blur-[100px] opacity-60"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-blue-100 rounded-full blur-[100px] opacity-60"></div>

        {/* Close Button */}
        {allowClose && (
          <button 
            onClick={onClose}
            className="absolute top-8 right-8 p-3 bg-white/80 hover:bg-white rounded-2xl shadow-sm border border-slate-100 text-slate-400 hover:text-slate-600 transition-all z-20"
          >
            <X className="w-6 h-6" />
          </button>
        )}

        <div className="relative z-10 p-8 sm:p-12 lg:p-16">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center p-3 bg-white rounded-2xl shadow-sm mb-6 border border-slate-100">
              <img src={TetfundLogo} alt="Logo" className="h-10 w-auto" />
            </div>
            <h1 className="text-4xl font-bold text-slate-900 mb-3 tracking-tight">Choose your account</h1>
            <p className="text-slate-500 text-lg max-w-md mx-auto leading-relaxed">
              Select the account you want to access for this session.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-6">
            {roles.map((role, index) => {
              const config = getRoleConfig(role.name);
              return (
                <button
                  key={index}
                  onClick={() => onSelect(role.name, role.roleId)}
                  className="group relative w-full max-w-sm bg-white border border-slate-200 rounded-3xl p-8 text-left transition-all duration-300 hover:border-emerald-500 hover:shadow-[0_20px_50px_rgba(16,185,129,0.1)] hover:-translate-y-2 overflow-hidden"
                >
                  {/* Subtle Gradient Hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/0 to-emerald-50/0 group-hover:from-emerald-50/50 group-hover:to-transparent transition-all duration-500"></div>
                  
                  <div className="relative z-10">
                    <div className={`inline-flex items-center justify-center p-4 rounded-2xl ${config.color} text-white mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      {config.icon}
                    </div>
                    
                    <div className="mb-8">
                        <h3 className="text-2xl font-bold text-slate-900 group-hover:text-emerald-700 transition-colors">{config.title}</h3>
                    </div>

                    <div className="flex items-center text-sm font-bold text-emerald-600 opacity-0 group-hover:opacity-100 transition-all duration-300 transform -translate-x-2 group-hover:translate-x-0">
                      Access Account <ArrowRight className="ml-2 w-4 h-4" />
                    </div>
                  </div>

                  {/* Decorative Element */}
                  <div className="absolute right-[-20px] bottom-[-20px] opacity-[0.03] group-hover:opacity-[0.08] transition-opacity duration-300">
                      {React.cloneElement(config.icon as React.ReactElement<any>, { size: 120 })}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSelectionModal;
